import React, { useEffect, useState, useCallback } from "react";
import { Snackbar, Alert, Button, Slide } from "@mui/material";
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const STORAGE_KEY = 'appBuildInfo';
const POLLING_INTERVAL = 60000; // 60 segundos

const UpdateNotification = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [buildInfo, setBuildInfo] = useState(null);

  // Cargar build-info.json desde el servidor
  const fetchBuildInfo = useCallback(async () => {
    try {
      // Cache busting con timestamp
      const response = await fetch(`/build-info.json?t=${Date.now()}`, {
        cache: 'no-store'
      });
      
      if (!response.ok) {
        console.warn('No se pudo cargar build-info.json');
        return null;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al cargar build-info.json:', error);
      return null;
    }
  }, []);

  // Comparar build-info del servidor vs localStorage
  const checkForUpdates = useCallback(async () => {
    const serverBuildInfo = await fetchBuildInfo();
    
    if (!serverBuildInfo) {
      return;
    }

    // Obtener build-info guardado en localStorage
    const storedBuildInfoStr = localStorage.getItem(STORAGE_KEY);
    
    // Si no hay build-info guardado, es la primera vez
    // Guardamos el build-info actual y no mostramos nada
    if (!storedBuildInfoStr) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serverBuildInfo));
      return;
    }

    try {
      const storedBuildInfo = JSON.parse(storedBuildInfoStr);
      
      // Comparar timestamp y versión
      const timestampDiff = serverBuildInfo.timestamp !== storedBuildInfo.timestamp;
      const versionDiff = serverBuildInfo.version !== storedBuildInfo.version;
      
      // Si hay diferencia, hay una nueva versión disponible
      if (timestampDiff || versionDiff) {
        setBuildInfo(serverBuildInfo);
        setShowNotification(true);
      }
    } catch (error) {
      console.error('Error al parsear build-info almacenado:', error);
      // Si hay error, guardamos el nuevo build-info
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serverBuildInfo));
    }
  }, [fetchBuildInfo]);

  useEffect(() => {
    // Verificar al montar el componente
    checkForUpdates();

    // Iniciar polling periódico
    const interval = setInterval(() => {
      checkForUpdates();
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [checkForUpdates]);

  const handleClose = () => {
    // NO permitir cerrar manualmente - el banner debe persistir hasta actualizar
    // setShowNotification(false);
  };

  const handleReload = () => {
    // Actualizar localStorage con el nuevo build-info antes de recargar
    if (buildInfo) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(buildInfo));
    }
    // Recargar la página
    window.location.reload();
  };

  // Formatear timestamp para mostrar
  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp.length !== 14) return timestamp;
    // YYYYMMDDHHMMSS -> YYYY-MM-DD HH:MM:SS
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    const hour = timestamp.substring(8, 10);
    const minute = timestamp.substring(10, 12);
    const second = timestamp.substring(12, 14);
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  };

  return (
    <Snackbar
      open={showNotification}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={Slide}
      sx={{
        // Asegurar que el banner esté siempre visible
        zIndex: 9999
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<SystemUpdateAltIcon fontSize="inherit" />}
        action={
          <Button 
            color="inherit" 
            size="small" 
            onClick={handleReload} 
            sx={{ 
              fontWeight: 'bold', 
              border: '1px solid rgba(255,255,255,0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            ACTUALIZAR
          </Button>
        }
        sx={{
          width: '100%',
          maxWidth: '600px',
          bgcolor: '#0288d1',
          '& .MuiAlert-message': { 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.5,
            width: '100%'
          }
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
          ¡Nueva versión disponible!
        </div>
        {buildInfo && (
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
            Versión: {buildInfo.version} | {formatTimestamp(buildInfo.timestamp)}
          </div>
        )}
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification;
